import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';

import { Subject } from 'rxjs';

import { intersectionWith } from 'lodash-es';

import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ImageFitMode } from '../../../../config/entities/picture-fill-mode.entity';
import { PartialRecord } from '../../../../core/public_api';
import { ActionGroup, ActionGroupModifier } from '../../../../elements/public_api';
import { LoadingBehaviorSubject } from '../../../../helpers/loading-behavior-subject.helper';
import { ShTableHeadComponent } from '../../../../overviews/generic-overview/components/table-head/table-head.component';
import { SelectionState } from '../../../generic-overview/components/context-action-bar/entities/select-mode.entity';
import { ShGenericGridItemComponent } from '../../components/generic-grid-item/generic-grid-item.component';
import { GridItemDef } from '../../entities/grid-item.entity';
import { GridType } from '../../entities/grid-type.entity';

@Component({
    standalone: true,
    selector: 'sh-generic-grid',
    templateUrl: './generic-grid.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ShTableHeadComponent, ShGenericGridItemComponent, TranslateModule],
    styleUrls: ['./generic-grid.component.scss']
})
export class ShGenericGridComponent implements OnDestroy {
    private destroy$ = new Subject<void>();

    _selectedElements: PartialRecord[] = [];
    _gridElements: (PartialRecord | GridItemDef)[] = [];
    _actionGroup: ActionGroup[] = [];
    _gridElementsActionGroups: ActionGroup[] = [];
    selectedPageItems!: number;
    loading$ = new LoadingBehaviorSubject(false);
    someRowsSelected = false;
    gridTypes = GridType;

    @Output() selectionChanged = new EventEmitter<SelectionState>();
    @Output() itemSelected = new EventEmitter<PartialRecord>();
    @Output() itemDeselected = new EventEmitter<PartialRecord>();
    @Output() itemClicked = new EventEmitter<PartialRecord>();
    @Output() thumbnailClicked = new EventEmitter<PartialRecord>();

    @Input() loadingMsg = '';
    @Input() loadingFailedMsg = '';
    @Input() emptyListMsg = '';
    @Input() gridType: GridType = GridType.GENERIC;
    @Input() hasError = false;
    @Input() availableSelect = false;
    @Input() disableSelect = false;
    @Input() disableSelectMessage = '';
    @Input() selectedItemsCount: number;
    @Input() itemsSelectedLabelKey?: string;
    @Input() noItemsSelectedLabelKey?: string;
    @Input() isInitialBulk: boolean;
    @Input() contextActionGroup?: ActionGroup;
    @Input() activeItem: PartialRecord | null;
    @Input() pictureFillMode: ImageFitMode = ImageFitMode.COVER;
    @Input() areItemsClickable: boolean;

    @Input() set isLoading(loading: boolean) {
        this.loading$.next(loading);
    }

    @Input() set gridElements(gridElements: (PartialRecord | GridItemDef)[]) {
        this._gridElements = gridElements;
        if (this._gridElements) this.setActionGroup();
        this.someRowsSelected = this._selectedElements.length > 0;
        this.setSelectedPageItems();
    }

    @Input() set actionItems(actionItems: ActionGroup[]) {
        this._actionGroup = actionItems;
        if (this._actionGroup && this._actionGroup.length > 0) this.setActionGroup();
    }

    @Input() canBatchSelect: boolean = false;

    @Input() set selectedElements(selected: PartialRecord[]) {
        if (selected && selected.length > 0) {
            this._selectedElements = selected;
        } else {
            this._selectedElements = [];
        }
        this.setSelectedPageItems();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    isGridItemSelected(item: PartialRecord): boolean {
        return (
            this.availableSelect &&
            !!this._selectedElements &&
            this._selectedElements.findIndex(selected => item.equals(selected)) > -1
        );
    }

    isGridItemActive(item: PartialRecord): boolean {
        return !!this.activeItem && this.activeItem.equals(item);
    }

    selectGridItem(item: PartialRecord, checked: boolean): void {
        if (checked) {
            this.itemSelected.emit(item);
        } else {
            this.itemDeselected.emit(item);
        }
    }

    clickGridItem(item: PartialRecord): void {
        this.itemClicked.emit(item);
    }

    clickGridItemOverlay(item: PartialRecord): void {
        this.thumbnailClicked.next(item);
    }

    onSelectionChanged(selectionState: SelectionState): void {
        this.selectionChanged.emit(selectionState);
    }

    trackByFunction(index: number, item: PartialRecord): any {
        return item.trackBy();
    }

    private setSelectedPageItems() {
        this.selectedPageItems = intersectionWith(this._selectedElements, this._gridElements, (x, y) =>
            x.equals(y)
        ).length;
    }

    private setActionGroup(): void {
        if (this._actionGroup) {
            this._gridElementsActionGroups = [];
            this._gridElements.forEach((item, index) => {
                this._gridElementsActionGroups.push({
                    ...(this._actionGroup && this._actionGroup[index]),
                    modifiers: [ActionGroupModifier.End]
                });
            });
        }
    }
}
